import { ref, computed } from 'vue'
import axios from '@/utils/axios'
import useSnackbar, { SnackbarType } from '@/composables/snackbar'
import useAcademySelection from '@/composables/useAcademySelection'
import { getAcademyDisplay, getContentPackItemIds } from '@/utils/contentPackAcademyDisplay'

export default function useExercises() {
  const RESOURCE = '/exercise'
  const { showSnackbar } = useSnackbar()
  const { getContentScopeParams } = useAcademySelection()

  const currentCategory = ref(null)

  const baseHeaders = [
    { text: 'NAME', value: 'name' },
    { text: 'TYPE', value: 'type' },
    {
      text: 'THUMBNAIL MALE', value: 'thumbnail', sortable: false, width: 130,
    },
    {
      text: 'THUMBNAIL FEMALE', value: 'thumbnailFemale', sortable: false, width: 130,
    },
    { text: 'VIDEO', value: 'video', sortable: false },
    {
      text: 'CREATOR', value: 'creator', sortable: true, width: 150,
    },
    {
      text: 'ACADEMY', value: 'academy', sortable: true, width: 150,
    },
    {
      text: 'ACTIVE', value: 'active', width: 100, class: 'compact-table-column', cellClass: 'compact-table-column',
    },
    {
      text: 'ACTIONS', value: 'actions', sortable: false, width: 100, align: 'center', class: 'compact-table-column', cellClass: 'compact-table-column',
    },
  ]

  const dtHeaders = computed(() => [...baseHeaders])

  const dtSearchQuery = ref('')
  const dtExpanded = ref([])

  const blankExercise = {
    id: '',
    uniqueKey: '',
    category: '',
    name: '',
    type: '',
    tags: [],
    searchTags: [],
    time: 0,
    thumbnail: '',
    thumbnailFemale: '',
    video: { url: '' },
    videoFemale: { url: '' },
    instruction: {
      imageUrl: '',
      imageUrlFemale: '',
      description: '',
    },
    tips: [],
    details: {
      instructions: {
        title: '',
        images: [],
      },
    },
    titleOfSession: '',
    sessionType: '',
    level: '',
    frequency: '',
    measurement: '',
    practiceType: '',
    goal: '',
    questionnaire: {
      title: '',
      whenToFill: '',
      ratingSource: '',
      sections: [
        {
          questionText: '',
          phrasing: '',
          choiceList: [
            {
              options: ['', ''],
            },
          ],
        },
      ],
    },
    exercisePriority: '',
    scoringDescriptions: [],
    assessmentTips: [],
    entryDataMethod: '',
    attempts: 0,
    distance: 0,
    distanceUnit: '',
    exerciseLevel: 1,
    presentInExercises: false,
  }

  const exercises = ref([])
  const exercise = ref(structuredClone(blankExercise))

  const isLoading = ref(false)

  const createExercise = async exerciseItem => {
    isLoading.value = true
    await axios
      .post(RESOURCE, exerciseItem)
      .then(() => {
        showSnackbar('You have successfully created a new exercise', SnackbarType.SUCCESS)
      })
      .catch(error => {
        const message = error.response?.data?.message ?? error.message
        const statusCode = error.response?.status ?? null
        showSnackbar(
          `Failed to create a new exercise:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
          SnackbarType.ERROR,
        )
        throw error
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const deleteExercise = async exerciseItem => {
    isLoading.value = true
    exerciseItem.deleted = true
    await axios
      .put(RESOURCE, exerciseItem)
      .then(() => {
        showSnackbar('You have successfully deleted the exercise', SnackbarType.SUCCESS)
      })
      .catch(error => {
        const message = error.response?.data?.message ?? error.message
        const statusCode = error.response?.status ?? null
        showSnackbar(
          `Failed to delete the exercise:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
          SnackbarType.ERROR,
        )
        throw error
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const fetchExercises = async (category = null) => {
    isLoading.value = true
    currentCategory.value = category
    const params = {
      withCreator: true,
      ...(category ? { category } : {}),
      ...getContentScopeParams(),
    }

    try {
      const [response, contentPacksResponse] = await Promise.all([
        axios.get(RESOURCE, { params }),
        axios.get('/content-pack').catch(() => ({ data: [] })),
      ])
      const contentPackExerciseIds = ['fitness', 'practice', 'flow'].reduce((result, sectionKey) => ({
        ...result,
        [sectionKey]: getContentPackItemIds(contentPacksResponse.data, sectionKey, 'exercises'),
      }), {})
      const filteredData = category
        ? response.data.filter(exerciseItem => exerciseItem.category === category)
        : response.data
      exercises.value = filteredData.map(exerciseItem => {
        const isPassFail = String(exerciseItem.category).toLowerCase() === 'practice'
          && exerciseItem.entryDataMethod === 'Goal vs Actual'
          && Number(exerciseItem.attempts) === 0

        return {
          ...exerciseItem,
          ...(isPassFail ? { entryDataMethod: 'Pass / Fail' } : {}),
          academy: getAcademyDisplay(
            exerciseItem,
            contentPackExerciseIds[String(exerciseItem.category || '').toLowerCase()] || new Set(),
          ),
        }
      })
    } catch (error) {
      // Preserve the existing silent handling for a failed exercise list request.
    } finally {
      isLoading.value = false
    }
  }

  const updateExercise = async exerciseItem => {
    isLoading.value = true
    await axios
      .put(RESOURCE, exerciseItem)
      .then(() => {
        showSnackbar('You have successfully updated the exercise', SnackbarType.SUCCESS)
      })
      .catch(error => {
        const message = error.response?.data?.message ?? error.message
        const statusCode = error.response?.status ?? null
        showSnackbar(
          `Failed to update the exercise:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
          SnackbarType.ERROR,
        )
        throw error
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const updateManyExercises = async (type, paramName, paramValue, ids) => {
    isLoading.value = true

    const payload = {
      type,
      params: [
        {
          name: paramName,
          value: paramValue,
          ids,
        },
      ],
    }

    try {
      await axios.put('/library/updateMany', payload)

      showSnackbar(`Successfully updated ${ids.length} item(s)`, SnackbarType.SUCCESS)

      return true
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      const statusCode = error.response?.status ?? null

      showSnackbar(`Failed to update items:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`, SnackbarType.ERROR)

      throw error
    } finally {
      isLoading.value = false
    }
  }

  const resetState = () => {
    exercises.value = []
    exercise.value = structuredClone(blankExercise)
    dtSearchQuery.value = ''
    dtExpanded.value = []
    isLoading.value = false
    currentCategory.value = null
  }

  return {
    currentCategory,
    dtHeaders,
    dtSearchQuery,
    dtExpanded,
    blankExercise,
    exercises,
    exercise,
    isLoading,
    createExercise,
    deleteExercise,
    fetchExercises,
    updateExercise,
    updateManyExercises,
    resetState,
  }
}
